import { Injectable, inject, DestroyRef } from '@angular/core';
import {
  Client,
  TokenRefresh,
  URLS,
} from 'src/app/core/constants/ConstantVariables';
import { ILogin } from 'src/app/models/auth/ILogin';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  interval,
  Observable,
  shareReplay,
  Subscription,
  tap,
  firstValueFrom,
  Subject,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Environment } from 'src/app/environment/environment';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { UserToken } from 'src/app/models/auth/UserToken';
import { GenericService } from '../generic/generic.service';
import { GenericGetAllResponse } from 'src/app/models/GenericGetAllResponse';
import { UserRight } from 'src/app/models/auth/UserRight';
import { OAuthService } from 'angular-oauth2-oidc';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { DialogService } from '../dialog/dialog.service';
import { CompanyDomain } from 'src/app/modules/auth/models/CompanyDomain';
import { AzureStorageService } from '../azure-storage/azure-storage.service';
import {
  CurrentClientService,
  getStoredEmployeeSelection,
} from '../current-client/current-client.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private oauthService = inject(OAuthService);
  private dialogService = inject(DialogService);
  private destroyRef = inject(DestroyRef);
  private currentClientService = inject(CurrentClientService);
  // Injected directly: UtilityService depends on AuthService through GenericService.
  private toastr = inject(ToastrService);
  private translate = inject(TranslateService);

  private triggerUpdateLogin = new Subject<boolean>();

  /**
   * Emits whenever the signed-in user's in-memory state is dropped (logout, a new login, a proxy-token
   * swap). Root services that hold per-employee data subscribe and clear themselves. They depend on
   * AuthService (through GenericService), so AuthService cannot call them without a circular injection.
   */
  readonly userStateCleared$ = new Subject<void>();
  primary_red: number = 0;
  primary_green: number = 0;
  primary_blue: number = 0;

  //isLogin: boolean = false;
  URLS: typeof URLS = URLS;
  private baseUrlSSO: string;
  private baseUrlEnwage: string;
  private baseSSOLoginUrl: string;
  public environment: Environment = new Environment();
  options = {
    headers: new HttpHeaders().set(
      'Content-Type',
      'application/x-www-form-urlencoded'
    ),
  };
  body = new URLSearchParams();
  UserController: string = '';
  jwthelper = new JwtHelperService();
  // userRights: UserRight[] = [];
  //rights: string[] = [];
  //rightsLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  companydetails: CompanyDomain | undefined;
  token?: UserToken;
  roleName: string = '';
  loggedInUserInfo: BehaviorSubject<any> = new BehaviorSubject<any>({});
  isLoggedIn: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isCompanyDomainApiLoading: boolean = false;
  rightsLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  editableEmployeeModules: any[] = [];
  employeeAccessModules: any[] = [];
  // Must match employee_template_configuration.module_name and the *appIsGranted argument in the side menu.
  readonly ONBOARDING_MODULE = 'Onboarding';
  enableSpanishLanguage: boolean = true;
  isSwipeClockSynced: boolean = false;
  // EN-18175 - set by get-employee-access for a terminated employee: view-only Summary, Paystub, Tax and
  // Documents (Annual Tax Forms only), landing on Paystub. The API rejects everything else with a 403.
  isTerminatedRestricted: boolean = false;
  private accessModulesRequest: Promise<void> | null = null;
  private accessModulesLoadedAt = 0;
  // EN-24306 - a refused client switch must not log the employee out; the token they hold is still valid.
  private isSwitchingClient = false;
  companyDomain: CompanyDomain = {
    companyDomain: '',
    primaryColor: '',
    secondaryColor: '',
    companyFavIcon: '',
    isDefault: true,
    companyLogo: null,
    companyId: 0,
    companyLoginBackground: null,
    companyLoginMain: null,
    companyLoginLogo: null,
    companyName: '',
    companyLoaderGif: '',
    companyLogoBase64: '',
  };

  constructor() {
    this.UserController = this.URLS.Controller.User.Name;
    this.baseUrlSSO = this.environment.getIdentityServerConnection();
    this.baseUrlEnwage = this.environment.getApiConnection() + 'api/v1/';
    this.baseSSOLoginUrl = this.environment.getSSOLoginConnection();

    this.oauthService.setStorage(localStorage);
    this.oauthService.configure({
      tokenEndpoint:
        this.environment.getIdentityServerConnection() + 'connect/token',
      clientId: Client.client_id,
      scope: 'EmployeePortalScope offline_access',
      dummyClientSecret: Client.client_secret,
      issuer: this.environment.getIdentityServerConnection(),
      strictDiscoveryDocumentValidation: false,
      requireHttps: false,
      showDebugInformation: true,
    });

    this.oauthService.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((e) => {
      if (this.isProxyToken()) {
        if (this.isActuallyExpired()) {
          this.LogOut();
        }
        return;
      }
        switch (e.type) {
          case 'token_expires':
            this.oauthService.refreshToken();
            break;
          case 'token_refresh_error':
            if (!this.isSwitchingClient) {
              this.recoverFromRefusedRefresh();
            }
            break;
        }
      });
  }

  private getTokenExpMs(): number {
    try {
      const raw = this.GetToken();
      if (!raw) return 0;
      const decoded: any = this.decodeProvidedToken(raw);
      return Number(decoded?.exp || 0) * 1000;
    } catch {
      return 0;
    }
  }

  isProxyToken(): boolean {
    try {
      const token = this.GetToken();
      if (!token) return false;
      const decoded: any = this.decodeProvidedToken(token);
      return String(decoded?.is_Proxy).toLowerCase() === 'true';
    } catch {
      return false;
    }
  }

  private isActuallyExpired(): boolean {
    const expMs = this.getTokenExpMs();
    return !expMs || Date.now() >= expMs;
  }

  EmptyLocalStorage() {
    localStorage.setItem('token', '');
    localStorage.setItem('refresh_token', '');
  }

  public getToken(): string {
    const token = localStorage.getItem('token');
    this.isLoggedIn.next(true);
    if (token != null) {
      return token;
    } else {
      return '';
    }
  }

  public getRefreshToken(): string {
    const token = localStorage.getItem('refresh_token');

    if (token != null) {
      return token;
    } else {
      return '';
    }
  }

  async Login(iLoginModel: ILogin): Promise<any> {
    // A fresh username/password login must not run on top of the previous session's leftovers. Tokens are
    // kept in localStorage, so after a few logins a stale/expired access_token (plus its refresh timer and
    // expiry bookkeeping) was still present; the library then acted on that existing state and the request
    // to /connect/token was never issued at all, leaving the login screen hanging. Clearing the stored
    // tokens first makes every login start from the same clean state an incognito window has.
    this.clearStoredSession();

    this.isLoggedIn.next(true);
    return await this.oauthService.fetchTokenUsingPasswordFlow(
      iLoginModel.username,
      iLoginModel.password
    );
  }

  /** Drops any previously stored OAuth session state without redirecting. */
  private clearStoredSession(): void {
    try {
      // logOut(true) = discard the local session only; it must not navigate away from the login screen.
      this.oauthService.logOut(true);
    } catch {
      // Nothing stored yet (or the library had no session) — nothing to clean up.
    }
    this.EmptyLocalStorage();
    this.clearUserState();
  }

  /**
   * Forgets everything held in memory about the signed-in user.
   *
   * This service is a root singleton and logout does not reload the page, so these outlived the
   * session: the decoded token is cached on first read (getDecodedAccessToken) and was never dropped,
   * so the next employee to sign in kept the previous one's name, roles and company flags; and
   * rightsLoaded stayed true, so the guards skipped loading the new employee's modules on an SSO
   * login and used the previous employee's menu and access.
   */
  private clearUserState(): void {
    this.userStateCleared$.next();
    this.token = undefined;
    this.loggedInUserInfo.next({});
    this.employeeAccessModules = [];
    this.editableEmployeeModules = [];
    this.isSwipeClockSynced = false;
    this.isTerminatedRestricted = false;
    this.accessModulesLoadedAt = 0;
    this.rightsLoaded.next(false);
    this.currentClientService.reset();
    // EN-24306 - the next login must not send the previous user's client on a refresh before it picks its own.
    this.currentClientService.setStoredSelection(null);
  }

  RefreshToken() {
    const token = this.GetToken();
    let headersList = new HttpHeaders();
    headersList = headersList.append('Authorization', `Bearer ${token}`);
    headersList = headersList.append(
      'Content-Type',
      'application/x-www-form-urlencoded'
    );
    this.options = { headers: headersList };
    this.body = new URLSearchParams();
    this.body.set('client_id', Client.client_id);
    this.body.set('client_secret', Client.client_secret);
    this.body.set('grant_type', Client.refresh_token);
    this.body.set('refresh_token', this.GetRefreshToken());
    return this.http.post(
      this.baseUrlSSO + URLS.login,
      this.body,
      this.options
    );
  }

  proxyTokenReplace() {
    this.oauthService.logOut();
    if (this.dialogService.openDialogsLength > 0) {
      this.dialogService.closeAll();
    }
    localStorage.clear();
    sessionStorage.clear();
    // The proxy token belongs to another employee.
    this.clearUserState();
  }

  LogOut() {
    this.oauthService.logOut();
    if (this.dialogService.openDialogsLength > 0) {
      this.dialogService.closeAll();
    }

    this.isLoggedIn.next(false);

    if (this.GetLoginFromSSO() == 'true') {
      window.location.href = this.baseSSOLoginUrl;
    } else {
      this.router.navigate(['/login']);
    }
    localStorage.clear();
    sessionStorage.clear();
    this.clearUserState();
  }

  public SetToken(token: any) {
    if (token != null && token != 'null' && token != '') {
      localStorage.setItem('access_token', token);
    } else {
      localStorage.setItem('access_token', '');
    }
  }

  public GetToken(): string {
    const token = localStorage.getItem('access_token');
    if (token != null) {
      return token;
    } else {
      return '';
    }
  }

  getDecodedAccessToken(): UserToken | null {
    if (this.token) {
      return this.token; // Use the cached token if available
    }
    try {
      const rawToken = this.GetToken();
      this.token = this.jwthelper.decodeToken(rawToken) as UserToken;

      // Parse roles and attach to token
      const userRoles = JSON.parse(this.token.User_Roles);
      this.token.roles = userRoles.map((obj: any) => obj.RoleName);

      return this.token;
    } catch {
      // Invalid token or parse error; return null as per contract
      return null;
    }
  }

  public isAuthenticated(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  public hexToRGB(hexColor: string): [number, number, number] {
    const hex = hexColor.replace('#', '');

    if (hex.length !== 6) {
      throw new Error('Invalid HEX color format');
    }

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return [r, g, b];
  }

  hexToHSL(hexColor: string): string {
    // Convert HEX to RGB
    const rgbColor = this.hexToRGB(hexColor);

    // Convert RGB to HSL
    return this.rgbToHSL(rgbColor);
  }

  public rgbToHSL([r, g, b]: [number, number, number]): string {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h,
      s,
      l = (max + min) / 2;

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      if (h) {
        h /= 6;
      }
    }

    if (h) {
      return `hsl(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(
        l * 100
      )}%)`;
    } else {
      return `hsl(${1 * 360}, ${s * 100}%, ${l * 100}%)`;
    }
  }

  public SetRefreshToken(token: any) {
    if (token != null && token != 'null' && token != '') {
      localStorage.setItem('refresh_token', token);
    } else {
      localStorage.setItem('refresh_token', '');
    }
  }

  public GetRefreshToken(): string {
    const token = localStorage.getItem('refresh_token');
    if (token != null) {
      return token;
    } else {
      return '';
    }
  }

  public SetLoginFromSSO(data: any) {
    localStorage.setItem('LoginFromSSO', data);
  }

  public GetLoginFromSSO() {
    const check = localStorage.getItem('LoginFromSSO');
    return check;
  }

  async getCompanyByDomainName(domain: any): Promise<any> {
    this.isCompanyDomainApiLoading = true;

    let response = await firstValueFrom(
      this.http.get<any>(
        this.baseUrlSSO +
          'api/' +
          this.URLS.Controller.Company.Name +
          this.URLS.Controller.Company.GetCompanyByDomainName +
          '?domain=' +
          domain
      )
    );

    if (response.Response != null) {
      this.isCompanyDomainApiLoading = false;
      this.companyDomain.primaryColor =
        response.Response.primaryColor ?? '#EC2227';
      this.companyDomain.companyDomain = response.Response.companyDomain ?? '';
      this.companyDomain.secondaryColor =
        response.Response.secondaryColor ?? '#ffffff';
      this.companyDomain.companyFavIcon =
        response.Response.companyFavIcon ?? '';
      this.companyDomain.isDefault = false;
      this.companyDomain.companyLogo = response.Response.companyLogo ?? null;
      this.companyDomain.companyId = response.Response.companyId ?? 0;
      this.companyDomain.companyLoginBackground =
        response.Response.companyLoginBackground ?? '';
      this.companyDomain.companyLoginMain =
        response.Response.companyLoginMain ?? '';
      this.companyDomain.companyLoginLogo =
        response.Response.companyLoginLogo ?? '';
      this.companyDomain.companyLoaderGif =
        response.Response.companyLoaderGif ?? '';
      this.companyDomain.companyName = response.Response.companyName;
    } else {
      this.isCompanyDomainApiLoading = false;
      this.companyDomain.primaryColor = '#EC2227';
      this.companyDomain.companyDomain = '';
      this.companyDomain.secondaryColor = '#ffffff';
      this.companyDomain.companyFavIcon = '';
      this.companyDomain.isDefault = true;
      this.companyDomain.companyLogo = null;
      this.companyDomain.companyId = 0;
      this.companyDomain.companyLoginBackground = '';
      this.companyDomain.companyLoginMain = '';
      this.companyDomain.companyLoginLogo = '';
      this.companyDomain.companyLoaderGif = '';
    }
    this.downloadCompanyLogoBase64();

    this.companydetails = this.companyDomain;
    if (this.companydetails?.primaryColor) {
      const primaryHSL = this.hexToHSL(this.companydetails?.primaryColor);
      const hslRegex = /hsl\((\d+),\s*(\d+%?),\s*(\d+%?)\)/;
      const match = primaryHSL.match(hslRegex);
      if (!match) {
        console.error(
          'Invalid primary color format. Use HSL format, e.g., "hsl(120, 100%, 50%)"'
        );
        return null;
      }
      const hue = parseInt(match[1]);
      const saturation = match[2];
      const lightness = parseInt(match[3]);
      const stepSize = 84 / (7 + 1);

      // lighter shades start
      // primary 500
      let ls = 500;
      for (let i = 1; i <= 7; i++) {
        const shadeLightness = lightness + i * stepSize;
        const shade = `hsl(${hue}, ${saturation}, ${shadeLightness}%)`;
        document.documentElement.style.setProperty('--primary-' + ls, shade);
        if (ls === 100) {
          ls = ls - 50;
        } else if (ls === 50) {
          ls = ls - 25;
        } else {
          ls = ls - 100;
        }
      }
      document.documentElement.style.setProperty('--primary-600', primaryHSL);

      const dhue = parseInt(match[1]);
      const dsaturation = match[2];
      const dlightness = parseInt(match[3]);

      // Calculate the step size to adjust the lightness for each shade
      const dstepSize = 20 / (3 + 1);
      let ld = 700;
      // Generate shades by adjusting the lightness
      for (let i = 1; i <= 3; i++) {
        const shadeLightness = dlightness - i * dstepSize;
        const shade = `hsl(${dhue}, ${dsaturation}, ${shadeLightness}%)`;
        document.documentElement.style.setProperty('--primary-' + ld, shade);
        ld = ld + 100;
      }

      // primary 700
      const shadeLightness700 = lightness - 1 * stepSize;
      const shade700 = `hsl(${hue}, ${saturation}, ${shadeLightness700}%)`;
      document.documentElement.style.setProperty('--primary-700', shade700);
      // primary 800
      const shadeLightness800 = lightness - 2 * stepSize;
      const shade800 = `hsl(${hue}, ${saturation}, ${shadeLightness800}%)`;
      document.documentElement.style.setProperty('--primary-800', shade800);
      // primary 900
      const shadeLightness900 = lightness - 3 * stepSize;
      const shade900 = `hsl(${hue}, ${saturation}, ${shadeLightness900}%)`;
      document.documentElement.style.setProperty('--primary-900', shade900);

      document.documentElement.style.setProperty(
        '--my-color',
        this.companydetails?.primaryColor
      );
      const red = parseInt(
        this.companydetails?.primaryColor.substring(1, 3),
        16
      ).toString();
      document.documentElement.style.setProperty('--primary-red', red);
      const green = parseInt(
        this.companydetails?.primaryColor.substring(3, 5),
        16
      ).toString();
      document.documentElement.style.setProperty('--primary-green', green);
      const blue = parseInt(
        this.companydetails?.primaryColor.substring(5, 7),
        16
      ).toString();
      document.documentElement.style.setProperty('--primary-blue', blue);
    } else {
      document.documentElement.style.setProperty('--my-color', 'red');
    }
  }

  downloadCompanyLogoBase64() {
    if (this.companyDomain?.companyLogo) {
      const absolutePath = this.companyDomain.companyLogo.split('/');
      let count = 0;
      let path = '';
      for (let index = absolutePath.length - 1; index >= 0; index--) {
        if (count == 0) {
          path = absolutePath[index];
        } else if (count < 4) {
          path = absolutePath[index] + '/' + path;
        }
        count++;
      }

      const url =
        this.baseUrlEnwage +
        this.URLS.Controller.BlobStorage.Name +
        this.URLS.Controller.BlobStorage.DownloadSSO +
        `?filename=${path}`;

      this.http
        .get(url, {
          responseType: 'blob',
        })
        .subscribe({
          next: (data) => {
            const reader = new FileReader();
            reader.readAsDataURL(data);
            reader.onloadend = () => {
              this.companyDomain.companyLogoBase64 = reader.result?.toString();
            };
          },
        });
    }
  }


  TriggerLogin() {
    this.triggerUpdateLogin.next(true);
  }

  SubscribeTriggerLogin() {
    return this.triggerUpdateLogin.asObservable().pipe(shareReplay());
  }
  companyUserType(
    name:
      | 'Staffing'
      | 'PEO'
      | 'Both'
      | 'None'
      | 'PayrollStaffing'
      | 'MultipleGroup'
  ): boolean {
    // Retrieve boolean values from token
    const isStaffing = this.token?.Is_Staffing === 'True';
    const isPEO = this.token?.Is_PEO === 'True';
    const isPayrollStaffing = this.token?.Is_Payroll_Staffing === 'True';
    const isMultipleGroup = this.token?.Is_Multiple_Group === 'True';

    // Return true for "None" if all values are false
    if (name === 'None') {
      return !isStaffing && !isPEO && !isPayrollStaffing && !isMultipleGroup;
    }

    // Handle individual company types
    if (name === 'Staffing') {
      return isStaffing;
    }

    if (name === 'PEO') {
      return isPEO;
    }

    if (name === 'PayrollStaffing') {
      return isPayrollStaffing;
    }

    if (name === 'MultipleGroup') {
      return isMultipleGroup;
    }

    if (name === 'Both') {
      return isStaffing && isPEO;
    }

    return false;
  }

  async getEmployeeAccessModules(): Promise<any> {
    if (localStorage.getItem('access_token')) {
      // EN-18175 - guards now call this on every navigation; concurrent callers share one request.
      if (!this.accessModulesRequest) {
        this.accessModulesRequest = this.loadEmployeeAccessModules().finally(() => {
          this.accessModulesRequest = null;
        });
      }
      await this.accessModulesRequest;
    } else {
      this.rightsLoaded.next(false);
      this.isLoggedIn.next(false);
    }
  }

  /**
   * EN-18175 - re-reads the employee's access on every page so a termination or rehire made by an admin
   * applies on the employee's next navigation, without a logout (AC5). A load finished within the last
   * second is reused, so the shell and module guards of one navigation do not each call the API.
   */
  async refreshEmployeeAccessModules(): Promise<void> {
    if (Date.now() - this.accessModulesLoadedAt < 1000 && this.rightsLoaded.value) {
      return;
    }
    await this.getEmployeeAccessModules();
  }

  private async loadEmployeeAccessModules(): Promise<void> {
    // Only the first load blanks the access; a refresh keeps the current menu until the new one arrives.
    if (!this.accessModulesLoadedAt) {
      this.rightsLoaded.next(false);
    }
    // EN-24306 - every login path (password, MFA, SSO deep link, page reload) loads the access modules
    // before landing, so the client is settled here, before the modules of the wrong row are read.
    await this.ensureClientSelection();
    let response = await this.fetchEmployeeAccess();
    if (response?.data?.isAccessExpired) {
      // EN-24306 - the client row this token acts as is past its client's access window (EN-18175's
      // access_duration_months). Move to another of the person's clients; with none left, the portal is closed.
      const midSession = !!this.accessModulesLoadedAt;
      if (!(await this.moveOffExpiredClient())) {
        this.toastr.error(this.translate.instant('Your Employee Portal access has ended.'));
        this.LogOut();
        return;
      }
      if (midSession) {
        // Pages and root services already hold the expired row's data; reload like a manual switch does.
        window.location.reload();
        return;
      }
      response = await this.fetchEmployeeAccess();
    }
    this.employeeAccessModules = response?.data?.showModules || [];
    this.editableEmployeeModules = response?.data?.editableModules || [];
    this.enableSpanishLanguage = response?.data?.enableSpanishLanguage;
    this.isSwipeClockSynced = response?.data?.isSwipeClockSynced;
    this.isTerminatedRestricted = !!response?.data?.isTerminatedRestricted;
    this.accessModulesLoadedAt = Date.now();
    this.rightsLoaded.next(true);
  }

  private fetchEmployeeAccess(): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(this.baseUrlEnwage + this.UserController + this.URLS.Controller.User.EmployeeAccess)
    );
  }

  /**
   * EN-24306 - reloads the client list (the expired row is no longer in it) and switches to its default.
   * True when the token now acts as a different, still-open row.
   */
  private async moveOffExpiredClient(): Promise<boolean> {
    const before = Number(this.decodeProvidedToken(this.GetToken())?.Employee_Id);
    this.currentClientService.reset();
    this.currentClientService.setStoredSelection(null);
    await this.ensureClientSelection();
    const after = Number(this.decodeProvidedToken(this.GetToken())?.Employee_Id);
    return after !== before && this.currentClientService.clients$.value.some((c) => c.employeeId === after);
  }

  /**
   * EN-24306 - picks the client this login works in: the stored selection when it is still one of the
   * login's clients, otherwise the default (earliest hire date). When the token acts as another row, SSO is
   * asked for a token for the chosen one. Runs once per login, only after MFA (an SSO refresh before MFA
   * would skip it), and never for an admin's proxy token, which is pinned to the employee it was issued for.
   */
  private async ensureClientSelection(): Promise<void> {
    const decoded: any = this.decodeProvidedToken(this.GetToken());
    const userId = decoded?.User_Id;
    if (!userId || this.isProxyToken() || !this.checkMFA() || this.currentClientService.isLoadedFor(userId)) {
      return;
    }

    const clients = await this.currentClientService.load(userId);
    if (clients === null) {
      // The list could not be read: keep the stored selection so refreshes stay on the chosen client.
      return;
    }
    const tokenRowListed = clients.some((c) => c.employeeId === Number(decoded?.Employee_Id));
    if (clients.length === 0 || (clients.length === 1 && tokenRowListed)) {
      // Nothing to switch between (ASO, single client, or every client's access window has passed). A lone row
      // that is the login's own needs no switchEmployeeId; one that isn't (the login's row is past its window)
      // must keep it, or the next refresh puts the token back on the expired login row. A lone row that is not
      // the token's own still gets switched to below.
      this.storeSelection(tokenRowListed ? clients[0].employeeId : null);
      if (tokenRowListed) {
        this.currentClientService.markCurrent(clients[0].employeeId);
      }
      return;
    }

    const stored = getStoredEmployeeSelection();
    const target = clients.find((c) => c.employeeId === stored) ?? clients.find((c) => c.isDefault) ?? clients[0];
    if (target.employeeId === Number(decoded?.Employee_Id)) {
      this.storeSelection(target.employeeId);
      this.currentClientService.markCurrent(target.employeeId);
      return;
    }

    if (!(await this.SwitchClient(target.employeeId))) {
      // Stay on the row the token acts as, and show that one, rather than a client whose data isn't loaded.
      const tokenEmployeeId = Number(this.decodeProvidedToken(this.GetToken())?.Employee_Id);
      if (clients.some((c) => c.employeeId === tokenEmployeeId)) {
        this.storeSelection(tokenEmployeeId);
        this.currentClientService.markCurrent(tokenEmployeeId);
      }
    }
  }

  /**
   * EN-24306 - a background refresh failed. When it asked for a stored client (switchEmployeeId), SSO may have
   * refused only that client (its access window ended, its link was removed, the portal was unreachable), so
   * try once more on the login's own row and reload onto it; anything else signs the employee out as before.
   */
  private async recoverFromRefusedRefresh(): Promise<void> {
    if (!getStoredEmployeeSelection()) {
      this.LogOut();
      return;
    }

    this.currentClientService.setStoredSelection(null);
    this.isSwitchingClient = true;
    try {
      await this.oauthService.refreshToken();
      window.location.reload();
    } catch {
      this.LogOut();
    } finally {
      this.isSwitchingClient = false;
    }
  }

  /** The row that holds the login; SSO issues tokens for it when no switchEmployeeId is sent. */
  private getLoginEmployeeId(): number | null {
    const decoded: any = this.decodeProvidedToken(this.GetToken());
    const id = Number(decoded?.Login_Employee_Id ?? decoded?.Employee_Id);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  /**
   * EN-24306 - stores the client to keep on refreshes. The login's own row is never stored: SSO issues it
   * without the switchEmployeeId header, so those refreshes don't depend on the portal confirming the row
   * (a slow or unreachable portal would otherwise refuse the refresh and sign the employee out).
   */
  private storeSelection(employeeId: number | null): void {
    this.currentClientService.setStoredSelection(
      employeeId != null && employeeId !== this.getLoginEmployeeId() ? employeeId : null
    );
  }

  /**
   * EN-24306 - asks SSO for a token acting as another of the login's client rows. The header interceptor adds
   * the stored selection as switchEmployeeId to the refresh grant, so this and every later automatic refresh
   * keep the chosen client. On a refusal the token stays as it was, and the selection goes back to the row
   * that token acts as - a selection SSO refuses would otherwise fail every later refresh and log them out.
   * A refresh that succeeds but still acts as another row (an SSO without the switch ignores the header) is a
   * refusal too: every API reads Employee_Id from the token, so calling it switched would show one client's
   * name over another client's data.
   */
  async SwitchClient(employeeId: number): Promise<boolean> {
    const currentEmployeeId = Number(this.decodeProvidedToken(this.GetToken())?.Employee_Id);
    const previous = this.currentClientService.clients$.value.some((c) => c.employeeId === currentEmployeeId)
      ? currentEmployeeId
      : null;
    this.storeSelection(employeeId);
    this.isSwitchingClient = true;
    try {
      await this.oauthService.refreshToken();
      this.token = undefined; // the decoded-token cache belongs to the previous row
      if (Number(this.decodeProvidedToken(this.GetToken())?.Employee_Id) !== employeeId) {
        this.storeSelection(previous);
        return false;
      }
      this.currentClientService.markCurrent(employeeId);
      return true;
    } catch {
      this.storeSelection(previous);
      return false;
    } finally {
      this.isSwitchingClient = false;
    }
  }

  // True when Onboarding is the employee's ONLY module. The API returns exactly this while onboarding is
  // still in progress (see UserRepository.GetEmployeeAcccess); every other tab is denied until an admin
  // approves them, so the portal has to land them on onboarding and keep them there.
  isOnboardingOnly(): boolean {
    return (
      this.employeeAccessModules.length === 1 &&
      this.employeeAccessModules[0] === this.ONBOARDING_MODULE
    );
  }

  // Where a signed-in employee belongs when no specific destination was requested.
  getLandingRoute(): string {
    // EN-33071 - a terminated employee lands on Summary like everyone else (the BA changed it from Paystub);
    // Summary is always in their restricted view. Also the "Go to" button on the not-available page.
    if (this.isTerminatedRestricted) {
      return '/summary';
    }
    return this.isOnboardingOnly() ? '/employee-onboarding' : '/summary';
  }

  GetProxyToken(employeeId: number, clientUserProxy: boolean = false) {
    let headersList = new HttpHeaders()
      .set('Content-Type', 'application/x-www-form-urlencoded')
      .set('isProxyRequired', 'true')
      .set('proxyRequiredFrom', employeeId.toString())
      .set('clientUserProxy', clientUserProxy.toString());
    this.options = { headers: headersList };
    this.body = new URLSearchParams();
    this.body.set('client_id', Client.client_id);
    this.body.set('client_secret', Client.client_secret);
    this.body.set('grant_type', Client.refresh_token);
    this.body.set('refresh_token', this.GetRefreshToken());
    return this.http.post(
      this.baseUrlSSO + URLS.login,
      this.body,
      this.options
    );
  }

  GetMFA_Token(code: string, token: string) {
    let headersList = new HttpHeaders()
      .set('Content-Type', 'application/x-www-form-urlencoded')
      .set('isProxyRequired', 'false')
      .set('isMFARequested', 'true')
      .set('inputCode', code.toString())
      .set('secretKey', this.getAccesTokenMS_Auth_Key() ?? '');
    this.options = { headers: headersList };
    this.body = new URLSearchParams();
    this.body.set('client_id', Client.client_id);
    this.body.set('client_secret', Client.client_secret);
    this.body.set('grant_type', Client.refresh_token);
    this.body.set('refresh_token', token);
    return this.http.post(
      this.baseUrlSSO + URLS.login,
      this.body,
      this.options
    );
  }

  private getAccesTokenMS_Auth_Key() {
    return localStorage.getItem('MSAuthAppKey');
  }
  GetMFAToken(verificationCode: string) {
    let AuthKey = this.getAccesTokenMS_Auth_Key();
    if (AuthKey == null) {
      return;
    }
    const token = this.GetToken();
    let headersList = new HttpHeaders();
    headersList = headersList.append('Authorization', `Bearer ${token}`);
    headersList = headersList.append(
      'Content-Type',
      'application/x-www-form-urlencoded'
    );

    // headersList = headersList.append('SecrateKey', AuthKey);
    // headersList = headersList.append('InputCode', verificationCode );
    this.options = { headers: headersList };
    this.body = new URLSearchParams();
    this.body.set('client_id', Client.client_id);
    this.body.set('client_secret', Client.client_secret);
    this.body.set('grant_type', Client.refresh_token);
    this.body.set('refresh_token', this.GetRefreshToken());
    return this.http.post(
      this.baseUrlSSO + URLS.login,
      this.body,
      this.options
    );
  }

  getAuthenticatorQR(name: string) {
    let Url: string = 'api/Authenticator/enable-mfa';
    return this.http.post(this.baseUrlSSO + Url, {
      userName: name,
    });
  }

  validateCode(code: string) {
    let secretKey = localStorage.getItem('MSAuthAppKey');
    let Url: string =
      'api/Authenticator/verify-code?secretKey=' +
      secretKey +
      '&inputCode=' +
      code;
    return this.http.get(this.baseUrlSSO + Url);
  }

  decodeProvidedToken(token: string) {
    return this.jwthelper.decodeToken(token) as UserToken;
  }

  CheckMFARequired(): boolean {
    const token = localStorage.getItem('access_token');
    const decodedToken: any = this.decodeProvidedToken(token ?? '');
    if (decodedToken) {
      return (
        decodedToken['Is_MFARequired'] === 'True' ||
        decodedToken['Is_MFARequired'] === true ||
        decodedToken['Is_MFARequired'] === 'true'
      );
    }
    let isMFAConfigMode = localStorage.getItem('isMFAConfigMode');
    if (token == null || token == '') {
      if (isMFAConfigMode == 'True' || isMFAConfigMode == 'true') {
        return true;
      }
      return false;
    }
    return false;
  }

  /**
   * Check if MFA is verified for the current user
   * @returns boolean - true if MFA is verified or not required, false otherwise
   */
  checkMFA(): boolean {
    const token = localStorage.getItem('access_token');
    const decodedToken: any = this.decodeProvidedToken(token ?? '');

    if (decodedToken) {
      const isMFARequired =
        decodedToken['Is_MFARequired'] === 'True' ||
        decodedToken['Is_MFARequired'] === true ||
        decodedToken['Is_MFARequired'] === 'true';
      const isMFAVerified =
        decodedToken['Is_MFAVerified'] === 'True' ||
        decodedToken['Is_MFAVerified'] === true ||
        decodedToken['Is_MFAVerified'] === 'true';

      if (isMFARequired) {
        return isMFAVerified;
      } else {
        return true;
      }
    }
    return false;
  }

  /**
   * Check if user can leave the MFA verification page
   * @returns boolean - true if MFA is verified, false to prevent navigation
   */
  canLeaveMFAVerification(): boolean {
    return this.checkMFA();
  }
}
